import { EnrollmentJointLayout } from '@egw/layouts'
import { Button, Alert, AlertType, GenericError, RadioGroup, ProgressBar, ModalDrawer } from '@egw/components'
import { Logger } from '@egw/logger'
import { WEALTH_ROUTES } from '@egw/constants'
import { type FC, useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
  type EnrollmentJointAccountTypeSubmitInput,
  useGetEnrollmentJointAccountTypeData,
  useSubmitEnrollmentJointAccountTypeData,
} from '@egw/api'
import { useNavigate } from 'react-router'
import { useTranslation } from '@egw/i18n'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { setContext } from '@egw/events'
import { enrollmentCookieService } from '@egw/enrollment-cookie'
import { riskService } from '@egw/risk-service'
import { EnrollmentPageLoading } from '../../../components/page-loading'
import { PageHeader } from '../../../components'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { JOINT_ACCOUNT_TYPE } from '../../../constants/joint-account-types'

const log = new Logger('Enrollment - Joint - AccountType')

interface FormData {
  accountType: 'JOINT_TENANTS_WITH_RIGHT_OF_SURVIVORSHIP' | 'TENANTS_IN_COMMON' | undefined
}

export const AccountTypePageV1: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const [isJtwrosModalOpen, setIsJtwrosModalOpen] = useState(false) // Type 1
  const [isTicModalOpen, setIsTicModalOpen] = useState(false) // Type 2
  const [isCommunityPropertyModalOpen, setIsCommunityPropertyModalOpen] = useState(false) // Type 3
  const [isJointTenantsEntiretyModalOpen, setIsJointTenantsEntiretyModalOpen] = useState(false) // Type 4

  const { data, isLoading, error } = useGetEnrollmentJointAccountTypeData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointAccountTypeData()

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - AccountType')

  // AUTO GENERATED FORM HANDLERS START
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      accountType: undefined,
    },
  })

  // Prefill form when data is available from API
  useEffect(() => {
    if (data?.accountType) {
      reset({
        accountType: data.accountType as FormData['accountType'],
      })
    }
  }, [data, reset])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid && formData.accountType) {
      try {
        // Reset enrollment session before initiating application
        await enrollmentCookieService.resetSession()
        await riskService.updateFlow('enrollment')
        const mutationData: EnrollmentJointAccountTypeSubmitInput = {
          accountType: formData.accountType,
          initiateApplication: true,
        }

        const response = await mutate(mutationData)

        // Check for API errors in the response
        if (response && handleEnrollmentErrors(response)) {
          return
        }

        // If path exists and is different
        if (response?.activePerson?.path && newPathIsDifferent(location.pathname, response.activePerson.path)) {
          // Set userId through context update
          setContext({ userId: response.activePerson.applicationId })
          void navigate(response.activePerson.path)
        } else if (!response?.activePerson?.path) {
          // If no path from BE, redirect to error
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })
  // AUTO GENERATED FORM HANDLERS END

  return (
    <EnrollmentJointLayout title={t('joint.account_type.page_title')}>
      {/* JTWROS MODAL */}
      <ModalDrawer
        isOpen={isJtwrosModalOpen}
        onClose={() => {
          setIsJtwrosModalOpen(false)
        }}
        title={t('joint.account_type.modals.jtwros.title')}
        testId="account-type-modal"
      >
        <div className="flex flex-col gap-md mt-sm">
          <ul className="list-disc list-inside space-y-2">
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.jtwros.description_1')}</li>
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.jtwros.description_2')}</li>
          </ul>
        </div>
      </ModalDrawer>
      {/* TIC MODAL */}
      <ModalDrawer
        isOpen={isTicModalOpen}
        onClose={() => {
          setIsTicModalOpen(false)
        }}
        title={t('joint.account_type.modals.tic.title')}
        testId="account-type-modal"
      >
        <div className="flex flex-col gap-md mt-sm">
          <ul className="list-disc list-inside space-y-2">
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.tic.description_1')}</li>
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.tic.description_2')}</li>
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.tic.description_3')}</li>
          </ul>
        </div>
      </ModalDrawer>

      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? <EnrollmentPageLoading testId="account_type-loading" /> : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          <PageHeader title={t('joint.account_type.page_title')} testId="page-header" />
          <ProgressBar showAnimation progressPercentage={(1 / 13) * 100} className="mb-md" testId="progress-bar" />
          <p className="typo-primary-base text-text-meta mb-xl" data-testid="description_1">
            {t('joint.account_type.description_1')}
          </p>
          <p className="typo-primary-base-semibold mb-sm" data-testid="account-type-title">
            {t('joint.account_type.account_type_title')}
          </p>
          <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
            <Controller
              name="accountType"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  options={[
                    {
                      value: JOINT_ACCOUNT_TYPE.JTWROS,
                      label: t(`joint.account_type.account_type_input_jtwros.label`),
                      icon: (
                        <InformationCircleIcon
                          className="w-5 h-5 text-text-action stroke-2 cursor-pointer"
                          onClick={() => {
                            setIsJtwrosModalOpen(true)
                          }}
                        />
                      ),
                    },
                    {
                      value: JOINT_ACCOUNT_TYPE.TIC,
                      label: t(`joint.account_type.account_type_input_tic.label`),
                      icon: (
                        <InformationCircleIcon
                          className="w-5 h-5 text-text-action stroke-2 cursor-pointer"
                          onClick={() => {
                            setIsTicModalOpen(true)
                          }}
                        />
                      ),
                    },
                  ]}
                  name="accountType"
                  className="!py-xs typo-primary-base"
                  testId="account-type-radio"
                />
              )}
            />

            <div className="flex-1 md:hidden" />

            {/* AUTO GENERATED COMPONENTS */}

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
